import axios from 'axios';

// Use environment variable for API URL, fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Create public axios instance (no auth headers)
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Request interceptor to add auth token and branch context
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('leadshive_token');
    const currentBranchId = localStorage.getItem('currentBranchId');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (currentBranchId) {
      config.headers['X-Branch-Id'] = currentBranchId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('leadshive_token');
      localStorage.removeItem('leadshive_user');
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Public API response interceptor (no redirect on 401)
publicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect to login for public API calls
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: async (email: string, password: string) => {
    // Use publicApi for login since user is not authenticated yet
    const response = await publicApi.post('/auth/login', { email, password });
    return response.data;
  },

  verify: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    localStorage.removeItem('leadshive_token');
    localStorage.removeItem('leadshive_user');
    return response.data;
  },

  register: async (userData: any) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  verifyEmail: async (token: string) => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  getStatus: async () => {
    const response = await api.get('/auth/status');
    return response.data;
  }
};

// User API calls
export const userAPI = {
  getProfile: async () => {
    const response = await api.get('/profile');
    return response.data;
  },

  updateProfile: async (firstName: string, lastName: string) => {
    const response = await api.put('/profile', { firstName, lastName });
    return response.data;
  },

  uploadAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.post('/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  requestPasswordReset: async () => {
    const response = await api.post('/profile/request-password-reset');
    return response.data;
  },

  updatePreferences: async (preferences: any) => {
    const response = await api.put('/user/preferences', preferences);
    return response.data;
  },

  updateMetrics: async (metrics: any) => {
    const response = await api.put('/user/metrics', metrics);
    return response.data;
  },

  getAllUsers: async () => {
    const response = await api.get('/user/all');
    return response.data;
  },

  updateUserRole: async (userId: string, role: string) => {
    const response = await api.put(`/user/${userId}/role`, { role });
    return response.data;
  },

  updateColumnSettings: async (columns: any[]) => {
    const response = await api.put('/user/column-settings', { columns });
    return response.data;
  },

  updateTableSettings: async (table: string, settings: any) => {
    const response = await api.put('/user/table-settings', { table, ...settings });
    return response.data;
  }
};

// Organization API calls
export const organizationAPI = {
  get: async () => {
    const response = await api.get('/organization');
    return response.data;
  },

  updateSettings: async (data: { name?: string; country?: string; industry?: string; isActive?: boolean; showLogoOnForms?: boolean }) => {
    const response = await api.put('/organization/settings', data);
    return response.data;
  },

  uploadLogo: async (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post('/organization/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  removeLogo: async () => {
    const response = await api.delete('/organization/logo');
    return response.data;
  }
};

// Leads API calls
export const leadsAPI = {
  getLeads: async () => {
    const response = await api.get('/leads');
    return response.data;
  },

  createLead: async (leadData: any) => {
    const response = await api.post('/leads', leadData);
    return response.data;
  },

  sendWelcomeEmail: async (leadId: number, templateId?: number) => {
    const response = await api.post(`/leads/${leadId}/send-welcome-email`, { templateId });
    return response.data;
  },

  updateLead: async (leadId: string, leadData: any) => {
    const response = await api.put(`/leads/${leadId}`, leadData);
    return response.data;
  },

  deleteLead: async (leadId: string) => {
    const response = await api.delete(`/leads/${leadId}`);
    return response.data;
  },

  getLeadStats: async () => {
    const response = await api.get('/leads/stats');
    return response.data;
  },

  getLeadById: async (leadId: string) => {
    const response = await api.get(`/leads/${leadId}`);
    return response.data;
  },

  getAttachedForms: async (leadId: string) => {
    const response = await api.get(`/leads/${leadId}/forms`);
    return response.data;
  },

  searchMatchingLeads: async (formData: any) => {
    const response = await api.post('/leads/search-matching', formData);
    return response.data;
  },

  createLeadFromForm: async (formData: any) => {
    const response = await api.post('/leads/create-from-form', formData);
    return response.data;
  },

  checkDuplicateEmail: async (email: string) => {
    const response = await api.post('/leads/check-duplicate-email', { email });
    return response.data;
  },

  getStages: async () => {
    const response = await api.get('/leads/stages');
    return response.data;
  },

  getSources: async () => {
    const response = await api.get('/leads/sources');
    return response.data;
  },

  getLeadCalls: async (leadId: string) => {
    const response = await api.get(`/leads/${leadId}/calls`);
    return response.data;
  },

  getLeadEmails: async (leadId: string) => {
    const response = await api.get(`/leads/${leadId}/emails`);
    return response.data;
  }
};

export default api;
export { publicApi };

// Analytics API calls
export const analyticsAPI = {
  getAutomationStats: async (params: { period?: string; start?: string; end?: string }) => {
    const query = new URLSearchParams();
    if (params.start && params.end) {
      query.set('start', params.start);
      query.set('end', params.end);
    } else if (params.period) {
      query.set('period', params.period);
    }
    const response = await api.get(`/analytics/automation-stats?${query.toString()}`);
    return response.data;
  }
};
